import * as Styles from "./styles";
import { useCallback, useState, useMemo, useEffect } from "react";
import { Menu, MenuItem, MenuDivider, SubMenu } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import {
  IconLogout,
  IconPeople,
  IconAdmin,
  IconAccountCircle,
  THEMES,
  AvatarWrapper,
  AVATAR_SIZES,
  Content,
  createIconWithPath,
  IconFlag,
  IconViewCard,
  withTooltip
} from "cdk-radial";
import { useTranslation } from "react-i18next";
import useProfileStore from "../core/UserProfileStore";
import { useUpdateTheme, useUpdateUserLocaleSettings, useUpdateAppView } from "./useUserSettings";
import {ThemeDetector} from '../theme/ThemeDetector';
import {  useOktaAuth } from "@okta/okta-react";
import { useHistory,  } from 'react-router-dom';
import styled from 'styled-components';
import "./menu.css";
import { useSideSheetStore } from "../components/SideSheet/useSideSheetStore";
import ProfileContainer from "../profile/ProfileContainer";
import {useWebViewStore} from "../webview/useWebViewStore";
import MeetMyTeamContainer from "../meet-my-team/MeetMyTeamContainer";
import {useGlobalStore} from "../core/GlobalStore";
import {
    AMPLITUDE_EVENTS,LOGOUT_URL,OKTA_LOGOUT_URL
} from '../../constants';
import {useAnalytics} from '../third-party-integration/amplitude';
import LogoutConfirmDialog from './LogoutConfirmDialog';
import { LOGOUT_STATUS, MESSAGES, useChannelMessage  } from "../channel-message/ChannelMessagingProvider";
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import useAudit from '../../hooks/useAudit';
import { focusHeaderIcon } from './focusHeaderIcon';
import { clearLocalStorageExceptDMS } from '../../util/clearLocalStorage';

export const generateTooltipProps = (
  iconName: string,
  displayName: string
) => ({
  id: `header-tooltip-${iconName}`,
  text: displayName,
  theme: THEMES.LIGHT,
  styles: { zIndex: 1002, display: window.innerWidth < 501 && "none" },
});

const ThemeIcon = createIconWithPath([
  {
    d: "M12,22 C17.5228475,22 22,17.5228475 22,12 C22,6.4771525 17.5228475,2 12,2 C6.4771525,2 2,6.4771525 2,12 C2,17.5228475 6.4771525,22 12,22 Z M12,20.5 L12,3.5 C16.6944204,3.5 20.5,7.30557963 20.5,12 C20.5,16.6944204 16.6944204,20.5 12,20.5 Z",
    fillRule: "evenodd",
  },
]);

export const StyledMenu = styled(Menu)`
  background-color: #222 !important;`

export const HeaderMenu = () => {
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  const { trackEvent, setTheme : setAnalyticsTheme, setLanguage  } = useAnalytics();
  const { mutate: updateTheme } = useUpdateTheme();
  const { resetActive  } = useWebViewStore();
  const { setIsLoggingOut,setLogoutMessage} = useGlobalStore();
  const { mutate: updateUserLocaleSettings } = useUpdateUserLocaleSettings();
  const { t,i18n } = useTranslation();
  const { setTheme, isEnterpriseAdmin,appView, locale, oktaUserProfile } = useProfileStore();
  const { openSideSheet } = useSideSheetStore();
  const theme = useProfileStore((state) => state.theme);
  const { logoutStatus, endPointLogout, notifyMessage } = useChannelMessage();
  const enableIdpProxyFlag = useFeatureFlag('UNIFY_ENABLE_IDP_PROXY');
  const { captureEvent } = useAudit();

  const nameAbbr = (oktaUserProfile?.profile?.firstName || '').substring(0,1) + (oktaUserProfile?.profile?.lastName || '').substring(0,1);
  const {mutate:updateAppView} = useUpdateAppView({
      onSuccess: () => {
        resetActive();
        history.push(`/unify/home`);
      },
  });
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const themeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const useSystemTheme = localStorage.getItem("use-system-theme") === "yes";
  const [systemTheme, setSystemTheme] = useState(useSystemTheme);

  const ProfileTitle = withTooltip(
    Styles.HeaderButton,
    generateTooltipProps("Profile", t("profile.toolTip"))
  );

  useEffect(()=>{
    if(locale){
      i18n.changeLanguage(locale);
      setLanguage(locale);
    }
  },[setLanguage,locale,i18n]);
  
  const toggleTheme = useCallback(
    (theme:any) => {
      setTheme(theme);
      updateTheme({ theme });
      setAnalyticsTheme(theme);
      notifyMessage(MESSAGES.UNIFY_PREFERENCE_CHANGED, { type: 'THEME', value: theme });
    },
    [updateTheme, setTheme, setAnalyticsTheme, notifyMessage]
  );

  const handleThemeChange = useCallback(
    (theme:any) => {
      localStorage.setItem("use-system-theme", theme === "THEME_SYSTEM" ? "yes" : "no");
      const isDarkMode = theme === "THEME_DARK" || (theme === "THEME_SYSTEM" && themeMediaQuery.matches);
      setSystemTheme(theme === "THEME_SYSTEM");
      trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Theme', 'Dark Mode': isDarkMode});
      localStorage.setItem("unify-theme", isDarkMode ? "dark" : "light");
      toggleTheme(isDarkMode ? "dark" : "light");
    },
    [themeMediaQuery, toggleTheme,trackEvent]
  );

  const handleLocaleUpdate = useCallback(
    (locale:any) => {
      localStorage.setItem("userLocale", locale);
      trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Language', 'Locale': locale});
      updateUserLocaleSettings({ locale });
      notifyMessage(MESSAGES.UNIFY_PREFERENCE_CHANGED, { type: 'LOCALE', value: locale });
    },
    [updateUserLocaleSettings,trackEvent,notifyMessage]
  );

  const handleAppViewUpdate = useCallback(
    (appView:any) => {
      trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Unify View', 'View': appView});
      updateAppView({ appView });

    },
    [updateAppView,trackEvent]
  );

  
  useEffect(() => {
     const handleLogout = async ()  =>{
      if (logoutStatus === LOGOUT_STATUS.APPS_COMPLETE) {
        const logoutMsg = t('home.logoutUnify');
        setLogoutMessage(logoutMsg);
        console.log("Logout completed, proceeding with unify logout");
        trackEvent(AMPLITUDE_EVENTS.LOGOUT_UNIFY,{'Action': 'Confirmed'});
        captureEvent({action: "Logout"});
        setIsLoggingOut(true);
        const idToken = oktaAuth.getIdToken();
        if (!idToken) {
          await oktaAuth.tokenManager.clear();
          clearLocalStorageExceptDMS();
          window.location.assign(`${LOGOUT_URL}?source=unify`);
          return;
        }

        if (enableIdpProxyFlag) {
            const unifyLogoutCallbackUrl = window.location.origin + '/logout/callback';
            const oktaDomainLogoutPath = `${OKTA_LOGOUT_URL}?id_token_hint=${encodeURIComponent(String(idToken))}&post_logout_redirect_uri=${encodeURIComponent(unifyLogoutCallbackUrl)}`;
            window.location.assign(oktaDomainLogoutPath);
            return;
        }

        await oktaAuth.tokenManager.clear();
        clearLocalStorageExceptDMS();

        await oktaAuth.signOut({
          postLogoutRedirectUri: `${LOGOUT_URL}?source=unify&id_token_hint=${encodeURIComponent(String(idToken))}`
        });

      } else if (logoutStatus === LOGOUT_STATUS.LOGOUT_PROGRESS) {
         const logoutMsg = t('home.logoutApplications');
          setLogoutMessage(logoutMsg);
          setIsLoggingOut(true);
        console.log("Logout completed, proceeding with unify logout in progress");
      }
    };
      handleLogout();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [logoutStatus]);

  const signOutUnify = async () => {
      setShowLogoutDialog(false);
      await endPointLogout();
  };

  const logout =  () => {
   setShowLogoutDialog(true);
   trackEvent(AMPLITUDE_EVENTS.LOGOUT_UNIFY,{'Action': 'Initiated'});
  }

  useEffect(() => {
    if (!systemTheme) return;
    const listener = (e:any) => toggleTheme(e.matches ? "dark" : "light");
    themeMediaQuery.addEventListener("change", listener);
    return () => themeMediaQuery.removeEventListener("change", listener);
  }, [systemTheme, themeMediaQuery, toggleTheme]);


  const handleDealerAdmin = () =>{
    trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Dealer Admin'});
    resetActive();
    history.push(`/unify/dealer-admin/feature-access`);
  }

  const handleMeetMyCdk = () =>{
    trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Meet My CDK Team'});
    openSideSheet({
      title:t('meetmycdkteam.title'),
      body:(<MeetMyTeamContainer/>),
      onClose: () => focusHeaderIcon('header-profilehelp'),
    })
  }

  const themeOptions = useMemo(
    () => [
      { value: "THEME_LIGHT", label: t("header.light"), testId: "header-menu-theme-light", checked: !useSystemTheme && theme === "light" },
      { value: "THEME_DARK", label: t("header.dark"), testId: "header-menu-theme-dark", checked: !useSystemTheme && theme === "dark" },
      { value: "THEME_SYSTEM", label: t("header.system"), testId: "header-menu-theme-system", checked: useSystemTheme },
    ],
    [t, theme, useSystemTheme]
  );

  const languageOptions = useMemo(
    () => [
      { value: "en_US", label: t("English (United States)"), testId: "header-menu-language-eus", checked: locale === "en_US" },
      { value: "en_CA", label: t("English (Canada)"), testId: "header-menu-language-eca", checked: locale === "en_CA" },
      { value: "fr_CA", label: t("French (Canada)"), testId: "header-menu-language-efr", checked: locale === "fr_CA" },
    ],
    [t, locale]
  );

  const preferenceOptions = useMemo(
    () => [
      { value: "default", label: t('tabs.default'), testId: "header-menu-app-default", checked: appView === "default" },
      { value: "unify", label: t('tabs.myUnify'), testId: "header-menu-app-unify", checked: appView === "unify" },
      { value: "applications", label: t('tabs.applications'), testId: "header-menu-app-applications", checked: appView === "applications" },
      { value: "workflows", label: t('tabs.workflows'), testId: "header-menu-app-workflows", checked: appView === "workflows" },
    ],
    [t, appView]
  );

  const menuItems = useMemo(
    () => [
      { value: "DEALER_ADMIN", label: t("header.dealerAdmin"), icon: <IconAdmin />, testId: "dealer-admin",click:handleDealerAdmin, show: isEnterpriseAdmin },
      { value: "MEET_MY_CDK_TEAM", label: t("header.meetMyCdkTeam"), icon: <IconPeople />, testId: "meetmyteam-menu",click:handleMeetMyCdk, show: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEnterpriseAdmin, t]
  );

  const onViewProfileClick = () => {
      trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'View Profile'});
      openSideSheet({
        title:t('profile.text'),
        dataTestId: 'profile-tab-header',
        body:(<ProfileContainer/>),
        onClose: () => focusHeaderIcon('header-profilehelp'),
      })
  };
  

  return (
    <>
      <StyledMenu
        boundingBoxPadding="5px 5px 5px 5px"
        portal
        align="start"
        id="szh-menu"
        transition
        className={theme ==='dark'? "my-custom-theme" :""}
        menuButton={
          <div data-testid="header-menu-profile-icon"
          onClick={() => trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, { 'Item': 'Profile' })}>
            <ProfileTitle
              backgroundAppearance={theme === "dark" ? 'light' : 'dark'}
              icon={<AvatarWrapper size={AVATAR_SIZES.SMALL} style={{ backgroundColor: "#F2F2F2" }}>
                <Content style={{ color: "black", cursor: "pointer" }}>{nameAbbr.toUpperCase()}</Content>
              </AvatarWrapper>}
              text={t("profile.toolTip")}
              dataTestId="header-profilehelp"
              data-track-label="Profile"
            />
          </div>
        }
      >
        <MenuItem data-testid="header-menu-profile" onClick={()=>onViewProfileClick()} key="PROFILE" value="PROFILE" tabIndex={0}>
          <IconAccountCircle />
          <Styles.MenuText>{t("header.viewProfile")}</Styles.MenuText>
        </MenuItem>

        <SubMenu label={<><ThemeIcon style={{ transform: "rotate(180deg)" }} /><Styles.MenuText data-testid="header-menu-theme">{t("header.theme")}</Styles.MenuText></>}>
          {themeOptions.map(({ value, label, testId, checked }) => (
            <MenuItem onClick={() => handleThemeChange(value)} key={value} value={value} >
              {checked && <Styles.CheckIcon />}
              <Styles.SubMenuText data-testid={testId} >{label}</Styles.SubMenuText>
            </MenuItem>
          ))}
        </SubMenu>

        <SubMenu label={<><IconFlag /><Styles.MenuText data-testid="header-menu-language">{t("header.language")}</Styles.MenuText></>}>
          {languageOptions.map(({ value, label, testId, checked }) => (
            <MenuItem onClick={() => handleLocaleUpdate(value)} key={value} value={value} >
              {checked && <Styles.CheckIcon />}
              <Styles.SubMenuText data-testid={testId}>{label}</Styles.SubMenuText>
            </MenuItem>
          ))}
        </SubMenu>

        <SubMenu label={<><IconViewCard /><Styles.MenuText data-testid="header-menu-app">{t("header.unifyView")}</Styles.MenuText></>}>
          {preferenceOptions.map(({ value, label, testId, checked }) => (
            <MenuItem onClick={() => handleAppViewUpdate(value)} key={value} value={value} >
              {checked && <Styles.CheckIcon />}
              <Styles.SubMenuText data-testid={testId}>{label}</Styles.SubMenuText>
            </MenuItem>
          ))}
        </SubMenu>

        {menuItems.filter((item) => item.show).map(({ value, label, icon, testId, click }) => (
          <MenuItem onClick={()=> click()} key={value} value={value} >
            {icon}
            <Styles.MenuText data-testid={testId}>{label}</Styles.MenuText>
          </MenuItem>
        ))}

        <MenuDivider />
        <MenuItem onClick={() => logout()} key="SIGNOUT" value="SIGNOUT">
          <IconLogout />
          <Styles.MenuText data-testid="header-menu-signout">{t("header.signout")}</Styles.MenuText>
        </MenuItem>
      </StyledMenu>

      <ThemeDetector onHandleThemeChange={toggleTheme} />

      <LogoutConfirmDialog
            isOpen={showLogoutDialog}
            data-testid="logout_dialog"     
            onLogout={async ()=>{await signOutUnify()}}
            onCancel={() => {setShowLogoutDialog(false)}}
      />
    </>
  );
};

